import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { renderAsync } from 'docx-preview';
import {
  DOCX_PAGE_WIDTH,
  SharePreviewProps,
  fixDocxBulletStyles,
} from 'components/ShareDocument/SharePreviewContent/sharePreviewHelpers';

const DocxPreview = ({ url }: SharePreviewProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const scale = useMemo(() => {
    if (!containerWidth || containerWidth >= DOCX_PAGE_WIDTH) {
      return 1;
    }

    return containerWidth / DOCX_PAGE_WIDTH;
  }, [containerWidth]);

  const shouldScale = scale < 1;

  useEffect(() => {
    setContainerWidth(null);
  }, [url]);

  useEffect(() => {
    if (!contentRef.current) {
      return;
    }

    let cancelled = false;
    const container = contentRef.current;
    container.innerHTML = '';
    setError('');
    setIsLoading(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load DOCX file');
        }
        return response.blob();
      })
      .then((blob) =>
        renderAsync(blob, container, undefined, {
          className: 'attachment-docx-content',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: true,
          renderHeaders: true,
          renderFooters: true,
        })
      )
      .then(() => {
        if (!cancelled) {
          fixDocxBulletStyles(container);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Unable to preview this DOCX file.');
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
      container.innerHTML = '';
    };
  }, [url]);

  // Measure the available width so a full-size page can be scaled down to fit.
  useLayoutEffect(() => {
    const updateWidth = () => {
      const width = contentRef.current?.clientWidth;

      if (width && width > 0) {
        setContainerWidth(width);
      }
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const element = contentRef.current;

    if (!element) {
      return;
    }

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [isLoading, url]);

  // Wrap the rendered content in a scale frame so a scaled page leaves no blank space.
  useLayoutEffect(() => {
    const container = contentRef.current;

    if (isLoading || !container) {
      return;
    }

    const resetWrapperStyles = (wrapper: HTMLElement) => {
      wrapper.style.transform = '';
      wrapper.style.transformOrigin = '';
      wrapper.style.width = '';
    };

    const unwrapScaleFrame = () => {
      const scaleFrame = container.querySelector<HTMLElement>(':scope > .attachment-docx-scale-frame');

      if (!scaleFrame) {
        return;
      }

      const contentWrapper = scaleFrame.querySelector<HTMLElement>('.attachment-docx-content-wrapper');

      if (contentWrapper) {
        resetWrapperStyles(contentWrapper);
        container.insertBefore(contentWrapper, scaleFrame);
      }

      scaleFrame.remove();
    };

    if (scale >= 1) {
      unwrapScaleFrame();
      return;
    }

    let contentWrapper = container.querySelector<HTMLElement>(':scope > .attachment-docx-content-wrapper');

    if (!contentWrapper) {
      contentWrapper = container.querySelector<HTMLElement>(
        '.attachment-docx-scale-frame > .attachment-docx-content-wrapper'
      );
    }

    if (!contentWrapper) {
      return;
    }

    let scaleFrame = container.querySelector<HTMLElement>(':scope > .attachment-docx-scale-frame');

    if (!scaleFrame) {
      scaleFrame = document.createElement('div');
      scaleFrame.className = 'attachment-docx-scale-frame';
      container.appendChild(scaleFrame);
      scaleFrame.appendChild(contentWrapper);
    } else if (contentWrapper.parentElement !== scaleFrame) {
      scaleFrame.appendChild(contentWrapper);
    }

    const naturalHeight = contentWrapper.scrollHeight;
    scaleFrame.style.width = `${DOCX_PAGE_WIDTH * scale}px`;
    scaleFrame.style.height = `${naturalHeight * scale}px`;
  }, [containerWidth, isLoading, scale, url]);

  return (
    <>
      {isLoading && <p className='paragraph-general mb-3'>Loading DOCX...</p>}
      {error && <p className='paragraph-general mb-3'>{error}</p>}
      <div
        ref={contentRef}
        className={`attachment-docx-wrapper attachment-docx-wrapper-contained${
          shouldScale ? ' attachment-docx-wrapper-scaled' : ''
        }`}
        style={(shouldScale ? { '--docx-scale': scale } : {}) as CSSProperties}
      />
    </>
  );
};

export default DocxPreview;
